import { format } from "date-fns";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Outlet } from "react-router";
import AIVisibilityFilters from "~/components/AIVisibilityFilters";
import AppSidebar from "~/components/AppSidebar";
import AuthLoader from "~/components/AuthLoader";
import PitchWatcher from "~/components/agencies/PitchWatcher";
import PastDueBanner from "~/components/billing/PastDueBanner";
import ViewerBanner from "~/components/ViewerBanner";
import PastDueNotice from "~/components/billing/PastDueNotice";
import NavigationHeader from "~/components/NavigationHeader";
import AIVisibilityUpsellBanner from "~/components/upsell/AIVisibilityUpsellBanner";
import type {
  CategoryDistribution,
  DomainMetric,
} from "~/components/ai-visibility/WhereAiGroundsAnswers";
import { SidebarInset, SidebarProvider } from "~/components/ui/sidebar";
import { Toaster } from "~/components/ui/sonner";
import {
  AIVisibilityFiltersProvider,
  useAIVisibilityFilters,
} from "~/context/AIVisibilityFiltersContext";
import { BreadcrumbProvider } from "~/context/BreadcrumbContext";
import { useAuth } from "~/context/AuthContext";
import {
  SubscriptionProvider,
  useSubscription,
} from "~/context/SubscriptionContext";
import { useScrollRestoration } from "~/hooks/useScrollRestoration";
import { generateColorMap } from "~/lib/ai-visibility";
import { apiClient } from "~/lib/api-client";
import { hasPlanFeature } from "~/lib/plan-features";
import { isPastDueSubscription } from "~/lib/subscription-status";
import { useLocation, useNavigate } from "~/lib/team-router";
import { cn } from "~/lib/utils";
import {
  computeSevenDayTrialWindow,
  isActiveTrialSubscription,
  isWithinFirstRunWindow,
} from "~/lib/trial";
import type { ChartDataEntry } from "~/types/AIVisibilityTypes";
import type { FunnelStage } from "~/types/PromptReviewTypes";

interface Conversation {
  id: string;
  title: string;
  created_at: string;
}

type BrandScore = {
  brand: string;
  score: number;
};

type RankedBrand = {
  brand: string;
  currentRank: number;
  previousRank: number;
  scoreChangePercent: number;
  currentScore: number;
};

interface Competitor {
  domain: string;
  brand_color: string;
}

interface BrandMetric {
  period: string;
  avg_mention_count: number;
  total_mention_count?: number;
  avg_share_of_voice: number;
  mention_rate: number;
  citation_rate: number;
  is_competitor: boolean;
  accuracy?: {
    accurate_count: number;
    inaccurate_count: number;
    unverifiable_count: number;
  };
  stance: {
    recommended_count: number;
    praised_count: number;
    neutral_count: number;
    criticized_count: number;
    warned_count: number;
  };
  context_role: {
    main_narrative_count: number;
    sources_references_count: number;
    disclaimer_count: number;
  };
}

type ApiResponse = {
  [brand: string]: BrandMetric[];
};

function getRanks(data: BrandScore[]): Record<string, number> {
  return Object.fromEntries(
    [...data]
      .sort((a, b) => b.score - a.score)
      .map((item, index) => [item.brand, index + 1])
  );
}

function processChartDataForBrands(rawData: ApiResponse): {
  newChartData: Record<string, Record<string, ChartDataEntry[]>>;
  result: Record<string, { max: number; min: number }>;
} {
  const newChartData: Record<string, Record<string, ChartDataEntry[]>> = {};
  const metricBounds: Record<string, { max: number; min: number }> = {};

  Object.entries(rawData).forEach(([brand, entries]) => {
    const formattedEntries = entries.map((entry) => {
      const { period, ...scores } = entry;
      return {
        date: period,
        scores,
      };
    });

    newChartData[brand] = {
      custom: formattedEntries,
    };
  });

  return { newChartData, result: metricBounds };
}

export type CrawlerStatusTotals = {
  requests: number;
  allowed_requests: number;
  redirect_requests: number;
  unsuccessful_requests: number;
  verified_requests: number;
  unverified_requests: number;
  edge_response_bytes: number;
  referrals: number;
};

type CrawlerTimeseriesPoint = CrawlerStatusTotals & {
  date: string;
};

export type DailyBotMetric = CrawlerStatusTotals & {
  date: string;
  display_name: string;
};

export type CrawlerBreakdownItem = CrawlerStatusTotals & {
  crawler_name: string;
  display_name: string;
  operator: string;
  category: string;
};

export type GroupedBreakdownItem = CrawlerStatusTotals & {
  display_name: string;
  operator: string;
};

type OperatorBreakdownItem = CrawlerStatusTotals & {
  operator: string;
};

export type CrawlerTopPathBot = CrawlerStatusTotals & {
  name: string;
  display_name: string;
  operator: string;
};

export type CrawlerTopPathItem = CrawlerStatusTotals & {
  path: string;
  hostname: string;
  path_kind: "sitemap" | "probe" | "endpoint" | "unlisted";
  bots: CrawlerTopPathBot[];
};

export type PathKindTotals = Partial<
  Record<"sitemap" | "probe" | "endpoint" | "unlisted", CrawlerStatusTotals>
>;

export type CategoryBreakdownItem = {
  category: string;
  label: string;
  requests: number;
};

export type CoverageStatus =
  | "covered"
  | "subdomains_only"
  | "primary_domain_missing"
  | "no_traffic"
  | "not_connected"
  | "unknown";

export type CrawlerCoverage = {
  status: CoverageStatus;
  checked_at: string | null;
  primary_domain: string | null;
  zone_name: string | null;
  hostnames: { hostname: string; requests: number }[];
};

type CloudflareIntegrationState = {
  zone_id: string | null;
  zone_name: string | null;
  last_synced_at: string | null;
  last_sync_status: string | null;
  last_sync_error: string | null;
  synced_through: string | null;
  supports_bot_verification: boolean | null;
};

export interface AIVisibilityData {
  brandOptions: string[];
  displayedBrandKeys: string[];
  industryRankings: RankedBrand[];
  selectedMetric: string;
  setSelectedMetric: (metric: string) => void;
  dataLoading: boolean;
  dataAvailable: boolean;
  isDataBeingCollected: boolean;
  currentPeriodChartData: Record<string, Record<string, ChartDataEntry[]>>;
  comparisonPeriodChartData: Record<string, Record<string, ChartDataEntry[]>>;
  compareEnabled: boolean;
  setCompareEnabled: (enabled: boolean) => void;
  comparisonFromDate: Date | null;
  comparisonToDate: Date | null;
  competitorsList: Competitor[];
  brandColorMap: Record<string, string>;
  teamDomain: string;
  onBrandColorSaved: (domain: string, color: string) => void;
  topDomainSources: {
    topDomains: DomainMetric[];
    categoryDistribution: CategoryDistribution[];
    loading: boolean;
  };
  metricsRawData: ApiResponse | null;
  funnelStageCounts: Record<FunnelStage, number> | null;
  crawlerAnalytics: {
    loading: boolean;
    isConnected: boolean;
    integration: CloudflareIntegrationState;
    timeseries: CrawlerTimeseriesPoint[];
    dailyBotMetrics: DailyBotMetric[];
    crawlerBreakdown: CrawlerBreakdownItem[];
    groupedBreakdown: GroupedBreakdownItem[];
    operatorBreakdown: OperatorBreakdownItem[];
    topPaths: CrawlerTopPathItem[];
    categoryBreakdown: CategoryBreakdownItem[];
    coverage: CrawlerCoverage;
    partialDay: string | null;
  };
}

function AIVisibilityLayoutContent() {
  const { user, updateUser } = useAuth();
  const { subscription, isLoading: subscriptionLoading } = useSubscription();
  const location = useLocation();
  const scrollContainerRef =
    useScrollRestoration<HTMLDivElement>("ai-visibility");
  const {
    selectedBrands,
    setSelectedBrands,
    llmId,
    countryCode,
    selectedTagIds,
    fromDate,
    toDate,
    datesReady,
    isBrandsInitialized,
    compareEnabled,
    setCompareEnabled,
    comparisonFromDate,
    comparisonToDate,
  } = useAIVisibilityFilters();

  const promptIdMatch = location.pathname.match(
    /\/ai-visibility\/prompts\/(\d+)/
  );
  const promptId = promptIdMatch ? promptIdMatch[1] : null;

  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [brandOptions, setBrandOptions] = useState<string[]>([]);
  const [displayedBrandKeys, setDisplayedBrandKeys] = useState<string[]>([]);
  const [industryRankings, setIndustryRankings] = useState<RankedBrand[]>([]);
  const [selectedMetric, setSelectedMetric] =
    useState<string>("mention_rate");
  const [metricsRawData, setMetricsRawData] = useState<ApiResponse | null>(
    null
  );
  const [funnelStageCounts, setFunnelStageCounts] = useState<Record<
    FunnelStage,
    number
  > | null>(null);
  const [dataLoading, setDataLoading] = useState(false);
  const [dataAvailable, setDataAvailable] = useState(true);
  const metricsFetchGenerationRef = useRef(0);
  const metricsRangeRef = useRef<{ from_date: string; to_date: string } | null>(
    null
  );
  const [isDataBeingCollected, setIsDataBeingCollected] = useState(false);
  const [currentPeriodChartData, setCurrentPeriodChartData] = useState<
    Record<string, Record<string, ChartDataEntry[]>>
  >({});
  const [comparisonPeriodChartData, setComparisonPeriodChartData] = useState<
    Record<string, Record<string, ChartDataEntry[]>>
  >({});
  const [competitorsList, setCompetitorsList] = useState<Competitor[]>([]);

  const activeTeam = user?.teams?.find(
    (team) => String(team.id) === String(user?.team_id)
  );
  const teamDomain = activeTeam?.domain ?? "";

  const brandColorMap = useMemo(
    () =>
      generateColorMap(brandOptions, {
        user: user ?? undefined,
        competitorsList,
      }),
    [brandOptions, user, competitorsList]
  );

  const handleBrandColorSaved = useCallback(
    (domain: string, color: string) => {
      if (domain === teamDomain && user?.teams) {
        updateUser({
          teams: user.teams.map((team) =>
            String(team.id) === String(user.team_id)
              ? { ...team, brand_color: color }
              : team
          ),
        });
      } else {
        setCompetitorsList((prev) =>
          prev.map((competitor) =>
            competitor.domain === domain
              ? { ...competitor, brand_color: color }
              : competitor
          )
        );
      }
    },
    [teamDomain, updateUser, user?.team_id, user?.teams]
  );
  const [topDomainSourcesLoading, setTopDomainSourcesLoading] = useState(false);
  const [topDomains, setTopDomains] = useState<DomainMetric[]>([]);
  const [categoryDistribution, setCategoryDistribution] = useState<
    CategoryDistribution[]
  >([]);
  const [crawlerAnalyticsLoading, setCrawlerAnalyticsLoading] = useState(false);
  const [crawlerAnalyticsConnected, setCrawlerAnalyticsConnected] = useState(false);
  const [crawlerTimeseries, setCrawlerTimeseries] = useState<
    CrawlerTimeseriesPoint[]
  >([]);
  const [dailyBotMetrics, setDailyBotMetrics] = useState<DailyBotMetric[]>([]);
  const [crawlerCoverage, setCrawlerCoverage] = useState<CrawlerCoverage>({
    status: "unknown",
    checked_at: null,
    primary_domain: null,
    zone_name: null,
    hostnames: [],
  });
  const [crawlerPartialDay, setCrawlerPartialDay] = useState<string | null>(
    null
  );
  const [crawlerBreakdown, setCrawlerBreakdown] = useState<
    CrawlerBreakdownItem[]
  >([]);
  const [groupedBreakdown, setGroupedBreakdown] = useState<
    GroupedBreakdownItem[]
  >([]);
  const [operatorBreakdown, setOperatorBreakdown] = useState<
    OperatorBreakdownItem[]
  >([]);
  const [crawlerTopPaths, setCrawlerTopPaths] = useState<CrawlerTopPathItem[]>(
    []
  );
  const [categoryBreakdown, setCategoryBreakdown] = useState<
    CategoryBreakdownItem[]
  >([]);
  const [crawlerIntegration, setCrawlerIntegration] =
    useState<CloudflareIntegrationState>({
      zone_id: null,
      zone_name: null,
      last_synced_at: null,
      last_sync_status: null,
      last_sync_error: null,
      synced_through: null,
      supports_bot_verification: null,
    });

  const isFirstRunHome =
    location.pathname === "/ai-visibility/home" &&
    (Boolean(activeTeam?.is_pitch) ||
      isWithinFirstRunWindow(activeTeam?.created_at));
  const needsUpgrade =
    !subscriptionLoading &&
    subscription?.is_ai_visibility_tracking_enabled === false;
  const isTrialEnded = Boolean(subscription?.trial_expired);
  const isPastDue = isPastDueSubscription(subscription);
  const showFrostedOverlay = needsUpgrade;
  const showDashboardBehindOverlay = needsUpgrade && isTrialEnded && !isPastDue;

  const sidebarOpen = (() => {
    const saved = localStorage.getItem("sidebar:state");
    return saved !== null ? saved === "true" : true;
  })();

  const calculateBrandScores = useCallback((rawData: ApiResponse): BrandScore[] => {
    const currentScores: BrandScore[] = [];

    Object.entries(rawData).forEach(([brand, values]) => {
      const list = values;
      if (list.length > 0) {
        let score: number;
        if (selectedMetric === "positive_sentiment") {
          const counts = list.reduce(
            (sum, item) => {
              const positive =
                (item.stance?.recommended_count ?? 0) +
                (item.stance?.praised_count ?? 0);
              return {
                positive: sum.positive + positive,
                total:
                  sum.total +
                  positive +
                  (item.stance?.neutral_count ?? 0) +
                  (item.stance?.criticized_count ?? 0) +
                  (item.stance?.warned_count ?? 0),
              };
            },
            { positive: 0, total: 0 }
          );
          if (counts.total === 0) return;
          score = Number(((counts.positive / counts.total) * 100).toFixed(1));
        } else {
          const total = list.reduce((sum, item) => {
            const metricVal = item[selectedMetric as keyof BrandMetric] ?? 0;
            return typeof metricVal === "number" ? sum + metricVal : sum;
          }, 0);
          score = Number((total / list.length).toFixed(2));
        }
        currentScores.push({ brand, score });
      }
    });
    return currentScores;
  }, [selectedMetric]);

  const updateIndustryRankings = useCallback(async (rawData: ApiResponse) => {
    const generation = metricsFetchGenerationRef.current;
    const isStale = () => generation !== metricsFetchGenerationRef.current;
    const isSentiment = selectedMetric === "positive_sentiment";

    const fetchEveryMention = async (
      from_date: string,
      to_date: string
    ): Promise<ApiResponse> => {
      const params = new URLSearchParams({
        from_date,
        to_date,
        group_by: "day",
        scope: "mentions",
      });
      if (llmId) params.append("answer_engine_id", llmId);
      if (countryCode) params.append("country", countryCode);
      if (promptId) {
        params.append("prompt_id", promptId);
      } else if (selectedTagIds.length > 0) {
        params.append("tag_ids", selectedTagIds.join(","));
      }
      const response = await apiClient.get(
        `/ai-visibility-metrics?${params.toString()}`
      );
      return response?.data?.data || {};
    };

    const range = metricsRangeRef.current;
    const scoreData =
      isSentiment && range
        ? await fetchEveryMention(range.from_date, range.to_date)
        : rawData;
    if (isStale()) return;

    const currentScores = calculateBrandScores(scoreData);
    const currentRanks = getRanks(currentScores);

    if (compareEnabled && comparisonFromDate && comparisonToDate) {
      const comparisonStartDate = comparisonFromDate;
      const comparisonEndDate = comparisonToDate;

      const prev_from_date = format(comparisonStartDate, "yyyy-MM-dd");
      const prev_to_date = format(comparisonEndDate, "yyyy-MM-dd");
      const past_params = new URLSearchParams({
        from_date: prev_from_date,
        to_date: prev_to_date,
        group_by: "day",
      });

      if (llmId) {
        past_params.append("answer_engine_id", llmId);
      }

      if (countryCode) {
        past_params.append("country", countryCode);
      }

      if (promptId) {
        past_params.append("prompt_id", promptId);
      } else {
        past_params.append("funnel_stages", "TOFU");
        if (selectedTagIds.length > 0) {
          past_params.append("tag_ids", selectedTagIds.join(","));
        }
      }

      const past_response = await apiClient.get(
        `/ai-visibility-metrics?${past_params.toString()}`
      );

      if (isStale()) return;

      const past_rawData: ApiResponse = past_response?.data?.data;
      const pastChartData: typeof currentPeriodChartData = {};

      Object.entries(past_rawData || {}).forEach(([brand, entries]) => {
        const formattedEntries = entries.map((entry) => {
          const { period, ...scores } = entry;
          return {
            date: period,
            scores,
          };
        });

        pastChartData[brand] = {
          custom: formattedEntries,
        };
      });

      setComparisonPeriodChartData(pastChartData);

      const previousScores = calculateBrandScores(
        isSentiment
          ? await fetchEveryMention(prev_from_date, prev_to_date)
          : past_rawData || {}
      );
      if (isStale()) return;

      const previousRanks =
        previousScores.length === 0 ? currentRanks : getRanks(previousScores);

      const final: RankedBrand[] = currentScores
        .map((current) => {
          const prev = previousScores.find((p) => p.brand === current.brand);
          const previousScore = prev?.score ?? 0;
          const percentageChange =
            previousScore === 0
              ? current.score === 0
                ? 0
                : 100
              : ((current.score - previousScore) / previousScore) * 100;
          return {
            brand: current.brand,
            currentScore: current.score,
            currentRank: currentRanks[current.brand],
            previousRank: previousRanks[current.brand],
            scoreChangePercent: Number(percentageChange.toFixed(2)),
          };
        })
        .sort((a, b) => a.currentRank - b.currentRank);

      if (isStale()) return;
      setIndustryRankings(final);
    } else {
      const final: RankedBrand[] = currentScores
        .map((current) => ({
          brand: current.brand,
          currentScore: current.score,
          currentRank: currentRanks[current.brand],
          previousRank: currentRanks[current.brand],
          scoreChangePercent: 0,
        }))
        .sort((a, b) => a.currentRank - b.currentRank);
      if (isStale()) return;
      setIndustryRankings(final);
      setComparisonPeriodChartData({});
    }
  }, [
    calculateBrandScores,
    compareEnabled,
    comparisonFromDate,
    comparisonToDate,
    llmId,
    countryCode,
    promptId,
    selectedMetric,
    selectedTagIds,
  ]);

  const selectedBrandsRef = useRef(selectedBrands);
  selectedBrandsRef.current = selectedBrands;
  const isBrandsInitializedRef = useRef(isBrandsInitialized);
  isBrandsInitializedRef.current = isBrandsInitialized;
  const userRef = useRef(user);
  userRef.current = user;
  const updateIndustryRankingsRef = useRef(updateIndustryRankings);
  updateIndustryRankingsRef.current = updateIndustryRankings;

  const fetchData = useCallback(async () => {
    if (subscriptionLoading) return;
    if (!datesReady) return;
    if (isFirstRunHome) return;
    if (needsUpgrade && !isTrialEnded) return;

    let effectiveFrom = fromDate;
    let effectiveTo = toDate;

    const shouldUseTrialWindow =
      isActiveTrialSubscription(subscription) || isTrialEnded;

    if (shouldUseTrialWindow) {
      const activeTeam = userRef.current?.teams?.find(
        (team) => team.id === userRef.current?.team_id
      );
      const window = computeSevenDayTrialWindow(activeTeam?.created_at);
      if (window.trialStartDate && window.trialEndDate) {
        effectiveFrom = window.trialStartDate;
        effectiveTo = window.trialEndDate;
      }
    }

    if (!effectiveFrom || !effectiveTo) return;

    const generation = ++metricsFetchGenerationRef.current;

    try {
      setDataLoading(true);
      setDataAvailable(true);
      const from_date = format(effectiveFrom, "yyyy-MM-dd");
      const to_date = format(effectiveTo, "yyyy-MM-dd");
      metricsRangeRef.current = { from_date, to_date };

      const params = new URLSearchParams({
        from_date: from_date,
        to_date: to_date,
        group_by: "day",
      });

      if (llmId) {
        params.append("answer_engine_id", llmId);
      }

      if (countryCode) {
        params.append("country", countryCode);
      }

      if (promptId) {
        params.append("prompt_id", promptId);
      } else {
        params.append("funnel_stages", "TOFU");
        if (selectedTagIds.length > 0) {
          params.append("tag_ids", selectedTagIds.join(","));
        }
      }

      const response = await apiClient.get(
        `/ai-visibility-metrics?${params.toString()}`
      );

      if (generation !== metricsFetchGenerationRef.current) return;

      setFunnelStageCounts(response?.data?.funnel_stage_counts ?? null);

      const rawData: ApiResponse = response?.data?.data;
      const emptyStateDomains: string[] = response?.data?.domains || [];
      if (Object.keys(rawData || {}).length === 0) {
        let fallbackDomains: string[] = Array.from(new Set(emptyStateDomains));
        if (fallbackDomains.length === 0) {
          const currentTeam = userRef.current?.teams?.find(
            (team) => team.id == userRef.current?.team_id
          );
          if (currentTeam?.domain) fallbackDomains = [currentTeam.domain];
        }

        setBrandOptions(fallbackDomains);
        setCurrentPeriodChartData({});
        setComparisonPeriodChartData({});
        setIndustryRankings([]);
        setMetricsRawData(null);

        if (!isBrandsInitializedRef.current) {
          if (fallbackDomains.length > 0) {
            setSelectedBrands(fallbackDomains);
          }
        }

        setDataLoading(false);
        setDataAvailable(false);

        const activeTeam = userRef.current?.teams?.find(
          (team) => team.id === userRef.current?.team_id
        );

        const trackingEnabled =
          subscription?.is_ai_visibility_tracking_enabled === true;
        setIsDataBeingCollected(
          trackingEnabled && isWithinFirstRunWindow(activeTeam?.created_at)
        );
        return;
      }

      setBrandOptions(Object.keys(rawData));
      setIsDataBeingCollected(false);
      setMetricsRawData(rawData);
      setDataAvailable(true);

      const availableBrands = Object.keys(rawData);
      const currentSelectedBrands = selectedBrandsRef.current;

      const validSelectedBrands = currentSelectedBrands.filter((brand) =>
        availableBrands.includes(brand)
      );

      if (!isBrandsInitializedRef.current || (currentSelectedBrands.length === 0 && availableBrands.length > 0)) {
        if (availableBrands.length > 0) {
          setSelectedBrands(availableBrands);
        } else if (validSelectedBrands.length !== currentSelectedBrands.length) {
          setSelectedBrands(validSelectedBrands, false);
        }
      } else {
        if (validSelectedBrands.length !== currentSelectedBrands.length) {
          setSelectedBrands(validSelectedBrands, false);
        }
      }

      const { newChartData } = processChartDataForBrands(rawData);
      setCurrentPeriodChartData(newChartData);

      await updateIndustryRankingsRef.current(rawData);

      if (generation !== metricsFetchGenerationRef.current) return;

      setDataLoading(false);
    } catch (err) {
      if (generation !== metricsFetchGenerationRef.current) return;
      setDataLoading(false);
      setDataAvailable(false);
      setIsDataBeingCollected(false);
      console.error("Failed to load data", err);
    }
  }, [
    fromDate,
    toDate,
    llmId,
    countryCode,
    subscriptionLoading,
    subscription,
    promptId,
    selectedTagIds,
    datesReady,
    isFirstRunHome,
    needsUpgrade,
    isTrialEnded,
    setSelectedBrands,
  ]);

  useEffect(() => {
    if (!subscriptionLoading) {
      fetchData();
    }
  }, [fetchData, subscriptionLoading]);

  const metricsRawDataRef = useRef(metricsRawData);
  metricsRawDataRef.current = metricsRawData;

  useEffect(() => {
    if (subscriptionLoading || !metricsRawDataRef.current) {
      return;
    }

    updateIndustryRankings(metricsRawDataRef.current);
  }, [selectedMetric, subscriptionLoading, updateIndustryRankings]);

  useEffect(() => {
    if (isFirstRunHome) return;

    const fetchCompetitors = async () => {
      try {
        const response = await apiClient.get("/competitors");
        if (
          response.data.data?.competitors &&
          Array.isArray(response.data.data.competitors)
        ) {
          setCompetitorsList(
            response.data.data.competitors.map((comp: { domain: string; brand_color?: string }) => ({
              domain: comp.domain,
              brand_color: comp.brand_color,
            }))
          );
        } else {
          setCompetitorsList([]);
        }
      } catch (error) {
        console.error("Failed to fetch competitors:", error);
        setCompetitorsList([]);
      }
    };
    fetchCompetitors();
  }, [location.pathname, isFirstRunHome]);

  useEffect(() => {
    if (selectedBrands.length > 0) {
      setDisplayedBrandKeys(selectedBrands);
    }
  }, [selectedBrands]);

  const fetchTopDomainSources = useCallback(async () => {
    const isSourcesPage = location.pathname.includes("/sources");
    const isPromptsPage = location.pathname.includes("/prompts") && !promptId;

    if (
      subscriptionLoading ||
      !datesReady ||
      !fromDate ||
      !toDate ||
      promptId ||
      isSourcesPage ||
      isPromptsPage ||
      isFirstRunHome ||
      (needsUpgrade && !isTrialEnded)
    )
      return;

    try {
      setTopDomainSourcesLoading(true);
      const from_date = format(fromDate, "yyyy-MM-dd");
      const to_date = format(toDate, "yyyy-MM-dd");

      const params = new URLSearchParams({
        from_date: from_date,
        to_date: to_date,
        domain_page: "1",
        domain_per_page: "20",
      });

      if (llmId) {
        params.append("answer_engine_id", llmId);
      }

      if (countryCode) {
        params.append("country", countryCode);
      }

      const response = await apiClient.get(
        `/url-domain-metrics?${params.toString()}`
      );

      const domainMetrics: DomainMetric[] = response.data.domain_metrics || [];
      setTopDomains(domainMetrics.slice(0, 10));

      const contentTypeDistribution: Record<string, number> =
        response.data.content_type_distribution || {};
      const domainCategoryDistribution: Record<string, number> =
        response.data.domain_category_distribution || {};

      const preferredDistribution =
        Object.keys(contentTypeDistribution).length > 0
          ? contentTypeDistribution
          : domainCategoryDistribution;

      const distribution = Object.entries(preferredDistribution)
        .map(([category, count]) => ({ category, count }))
        .filter((item) => item.count > 0)
        .sort((a, b) => b.count - a.count);

      setCategoryDistribution(distribution);
    } catch (error) {
      console.error("Failed to fetch domain sources:", error);
      setTopDomains([]);
      setCategoryDistribution([]);
    } finally {
      setTopDomainSourcesLoading(false);
    }
  }, [
    fromDate,
    toDate,
    datesReady,
    llmId,
    countryCode,
    subscriptionLoading,
    promptId,
    location.pathname,
    isFirstRunHome,
    needsUpgrade,
    isTrialEnded,
  ]);

  useEffect(() => {
    if (!subscriptionLoading) {
      fetchTopDomainSources();
    }
  }, [fetchTopDomainSources, subscriptionLoading]);


  const fetchCrawlerAnalytics = useCallback(async () => {
    if (!datesReady || !fromDate || !toDate || isFirstRunHome) {
      return;
    }
    if (
      subscriptionLoading ||
      !hasPlanFeature(subscription, "ai_traffic_analytics")
    ) {
      setCrawlerAnalyticsLoading(false);
      setCrawlerAnalyticsConnected(false);
      return;
    }

    try {
      setCrawlerAnalyticsLoading(true);
      const from_date = format(fromDate, "yyyy-MM-dd");
      const to_date = format(toDate, "yyyy-MM-dd");

      const response = await apiClient.get(
        `/ai-visibility-crawler-metrics?from_date=${from_date}&to_date=${to_date}`
      );
      const data = response.data.data || {};

      setCrawlerAnalyticsConnected(Boolean(data.is_connected));
      setCrawlerIntegration({
        zone_id: data.integration?.zone_id || null,
        zone_name: data.integration?.zone_name || null,
        last_synced_at: data.integration?.last_synced_at || null,
        last_sync_status: data.integration?.last_sync_status || null,
        last_sync_error: data.integration?.last_sync_error || null,
        synced_through: data.integration?.synced_through || null,
        supports_bot_verification:
          data.integration?.supports_bot_verification ?? null,
      });
      setCrawlerTimeseries(data.timeseries || []);
      setDailyBotMetrics(data.daily_bot_metrics || []);
      setCrawlerBreakdown(data.crawler_breakdown || []);
      setGroupedBreakdown(data.grouped_breakdown || []);
      setOperatorBreakdown(data.operator_breakdown || []);
      setCrawlerTopPaths(data.top_paths || []);
      setCategoryBreakdown(data.category_breakdown || []);
      setCrawlerCoverage({
        status: data.coverage?.status || "unknown",
        checked_at: data.coverage?.checked_at || null,
        primary_domain: data.coverage?.primary_domain || null,
        zone_name: data.coverage?.zone_name || null,
        hostnames: data.coverage?.hostnames || [],
      });
      setCrawlerPartialDay(data.partial_day || null);
    } catch (error) {
      console.error("Failed to fetch crawler analytics:", error);
      setCrawlerAnalyticsConnected(false);
      setCrawlerTimeseries([]);
      setDailyBotMetrics([]);
      setCrawlerBreakdown([]);
      setGroupedBreakdown([]);
      setOperatorBreakdown([]);
      setCrawlerTopPaths([]);
      setCategoryBreakdown([]);
    } finally {
      setCrawlerAnalyticsLoading(false);
    }
  }, [
    fromDate,
    toDate,
    datesReady,
    subscriptionLoading,
    subscription,
    isFirstRunHome,
  ]);

  useEffect(() => {
    fetchCrawlerAnalytics();
  }, [fetchCrawlerAnalytics]);

  const outletContext: AIVisibilityData = {
    brandOptions,
    displayedBrandKeys,
    industryRankings,
    selectedMetric,
    setSelectedMetric,
    dataLoading: dataLoading || !datesReady,
    dataAvailable,
    isDataBeingCollected,
    currentPeriodChartData,
    comparisonPeriodChartData,
    compareEnabled,
    setCompareEnabled,
    comparisonFromDate,
    comparisonToDate,
    competitorsList,
    brandColorMap,
    teamDomain,
    onBrandColorSaved: handleBrandColorSaved,
    topDomainSources: {
      topDomains,
      categoryDistribution,
      loading: topDomainSourcesLoading || !datesReady,
    },
    metricsRawData,
    funnelStageCounts,
    crawlerAnalytics: {
      loading: crawlerAnalyticsLoading || !datesReady,
      isConnected: crawlerAnalyticsConnected,
      integration: crawlerIntegration,
      timeseries: crawlerTimeseries,
      dailyBotMetrics,
      crawlerBreakdown,
      groupedBreakdown,
      operatorBreakdown,
      topPaths: crawlerTopPaths,
      categoryBreakdown,
      coverage: crawlerCoverage,
      partialDay: crawlerPartialDay,
    },
  };

  if (!user) {
    return null;
  }

  return (
    <SidebarProvider
      className="h-screen overflow-hidden"
      style={
        {
          "--sidebar-width": "calc(var(--spacing) * 72)",
          "--header-height": "calc(var(--spacing) * 12)",
        } as React.CSSProperties
      }
    >
      <AppSidebar
        user={user}
        sidebarOpen={sidebarOpen}
        conversations={conversations}
        setConversations={setConversations}
      />
      <SidebarInset className="flex min-h-0 flex-1 flex-col overflow-hidden">
        <div className="shrink-0">
          <div className="hidden h-12 items-center md:flex">
            <div className="min-w-0 flex-1">
              <NavigationHeader conversations={conversations} />
            </div>
            <div className="shrink-0 pr-4">
              {!isFirstRunHome && (
                <AIVisibilityFilters
                  brandOptions={brandOptions}
                  brandColorMap={brandColorMap}
                  teamDomain={teamDomain}
                  onBrandColorSaved={handleBrandColorSaved}
                  className="flex-wrap justify-end"
                />
              )}
            </div>
          </div>

          <div className="md:hidden">
            <NavigationHeader conversations={conversations} />
            {!isFirstRunHome && (
              <div className="overflow-x-auto overscroll-x-contain px-4 pb-2.5 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
                <AIVisibilityFilters
                  brandOptions={brandOptions}
                  brandColorMap={brandColorMap}
                  teamDomain={teamDomain}
                  onBrandColorSaved={handleBrandColorSaved}
                  className="w-max flex-nowrap"
                />
              </div>
            )}
          </div>
        </div>
        <PastDueBanner />
        <ViewerBanner />
        <PitchWatcher />

        <main className="flex min-h-0 flex-1 flex-col">
          <div
            ref={scrollContainerRef}
            className={cn(
              "flex min-h-0 flex-1 flex-col gap-2 overflow-x-hidden overflow-y-auto",
              showFrostedOverlay ? "@container-[size]/main" : "@container/main"
            )}
          >
            <div
              className={cn(
                showFrostedOverlay
                  ? "grid w-full min-h-full shrink-0"
                  : "min-h-0 flex-1"
              )}
            >
              {showFrostedOverlay ? (
                showDashboardBehindOverlay && (
                  <div className="col-start-1 row-start-1">
                    <Outlet context={outletContext} />
                  </div>
                )
              ) : (
                <Outlet context={outletContext} />
              )}
              {showFrostedOverlay && (
                <div
                  className={cn(
                    "sticky top-0 z-10 col-start-1 row-start-1 flex h-[100cqh] items-start justify-center self-start overflow-y-auto px-4 pt-16 pb-24",
                    showDashboardBehindOverlay
                      ? "bg-white/50 backdrop-blur-[3px]"
                      : "bg-stone-50/90"
                  )}
                >
                  <div className="w-full max-w-lg">
                    {isPastDue ? (
                      <PastDueNotice />
                    ) : (
                      <AIVisibilityUpsellBanner isTrialEnded={isTrialEnded} />
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </main>
      </SidebarInset>
    </SidebarProvider>
  );
}

export default function AIVisibilityLayout() {
  const { isLoading, isAuthenticated, user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      navigate("/login");
    }
  }, [isAuthenticated, isLoading, navigate]);

  if (isLoading) {
    return <AuthLoader />;
  }

  if (!isAuthenticated) {
    return null;
  }

  return (
    <SubscriptionProvider>
      <BreadcrumbProvider>
        <AIVisibilityFiltersProvider key={user?.team_id}>
          <Toaster richColors position="top-center" />
          <AIVisibilityLayoutContent />
        </AIVisibilityFiltersProvider>
      </BreadcrumbProvider>
    </SubscriptionProvider>
  );
}
